import { useEffect, useState, useCallback } from 'react'
import { UnpaidDocumentReservationFragment, UnpaidDocumentsFragment } from 'src/api/api.generated'
import { UnpaidDocumentRow, UnpaidDocumentsPaidInFullQuotasType } from './payment-distribution.types'
import { HicDataGridPro } from '../../hic-vapor/datagrid/datagrid'
import { GridColDef, GridSortDirection, GridSortModel } from '@mui/x-data-grid-pro'
import { keysOf } from 'src/utils/utils'
import HicAmountField from '../../hic-amount-field/hic-amount-field'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { useTranslation } from 'react-i18next'
import { useConfirm } from 'hic-components'
import { DocumentPaymentSchema } from '../../../pages/accounting/documents/checkout-document/components/paid-in-full/paid-in-full-document-form.hook'
import { HICError } from '../../typography/error-text'
import { Box, Divider, Stack, Tooltip, Typography } from '@vapor/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { compareDates } from 'src/utils/date'
import { faHourglassHalf } from '@fortawesome/pro-solid-svg-icons'

type PaymentDistributionProps = {
  unpaidDocuments: UnpaidDocumentsFragment[]
  paidAmount: number
  /** Payment Active types UNPAID */
  unpaidPaymentActives: string[]
  onTotalPaidAmountChange: (amount: number) => void
  onChange: (documentPayments: DocumentPaymentSchema[]) => void
  error?: string
  /** Allow to maintain this document as first row of table. Useful when the view is opened from an unpaid document. */
  documentIdOnTop?: string
  readonly?: boolean
  paidInFullDocumentsQuotas?: UnpaidDocumentsPaidInFullQuotasType
}

/**
 * Manages the distribution of a paid amount, on unpaid documents.
 */
export function PaymentDistribution(props: PaymentDistributionProps) {
  const { unpaidDocuments, paidAmount, unpaidPaymentActives, onTotalPaidAmountChange, onChange, error, documentIdOnTop, readonly, paidInFullDocumentsQuotas } = props
  const { t } = useTranslation()
  const confirm = useConfirm()

  const [rows, setRows] = useState<UnpaidDocumentRow[]>([])
  const [isTouch, setIsTouch] = useState(false)
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [localPaidAmount, setLocalPaidAmount] = useState(0)

  useEffect(() => {
    setLocalPaidAmount(paidAmount)
  }, [paidAmount])

  const onChangeHandler = useCallback(
    (updRows: UnpaidDocumentRow[]) => {
      // Payments Document
      const updDocumentPayments = updRows.reduce((result: DocumentPaymentSchema[], current: UnpaidDocumentRow) => {
        if (current.paidAmountQuota > 0) return [...result, { referenceDocumentId: current.id, amount: current.paidAmountQuota, unpaidAmount: current.unpaidAmount }]
        return result
      }, [])
      onChange(updDocumentPayments)
    },
    [onChange],
  )

  /** Calculate distribution of paid amount on rows */
  const calculateQuotas = useCallback(
    (amount: number) => {
      const result: UnpaidDocumentRow[] = []
      rows?.reduce((residualAmount: number, r) => {
        if (residualAmount >= r.unpaidAmount) {
          result.push({ ...r, paidAmountQuota: r.unpaidAmount })
          return residualAmount - r.unpaidAmount
        } else {
          result.push({ ...r, paidAmountQuota: residualAmount })
          return 0
        }
      }, amount)
      onChangeHandler(result)
      return result
    },
    [rows, onChangeHandler],
  )

  /** Apply paid amount quotas on documents */
  const initializeRows = (documents: UnpaidDocumentsFragment[], unpaidPA: string[], paidInFullDocumentsQuotas?: UnpaidDocumentsPaidInFullQuotasType) => {
    const result: UnpaidDocumentRow[] = []
    documents.forEach((ud) => {
      const unpaidDocumentPaymentDetails = ud.documentPaymentDetails?.filter((d) => unpaidPA.includes(d.paymentActiveId))
      if (unpaidDocumentPaymentDetails && unpaidDocumentPaymentDetails.length && unpaidDocumentPaymentDetails[0].expireDate) {
        const pendingPaidInFullDocuments = paidInFullDocumentsQuotas?.[ud.id]
        const pendingPaymentAmount = pendingPaidInFullDocuments ? pendingPaidInFullDocuments.quotas?.reduce((t, q) => t + q.amount, 0) : 0
        const reservation =
          ud?.bills && ud?.bills?.every((bill) => bill?.reservation?.length === 1 && bill?.reservation?.[0]?.id === ud.bills?.[0]?.reservation?.[0]?.id)
            ? ud.bills?.[0]?.reservation?.[0] ?? null
            : null

        result.push({
          id: ud.id,
          numberExtended: ud.numberExtended,
          documentDate: ud.documentDate,
          totalAmount: ud.totalAmount,
          unpaidAmount: ud.unpaidAmount - pendingPaymentAmount,
          residualAmount: (ud.residualAmount ?? 0) - pendingPaymentAmount,
          expireDate: unpaidDocumentPaymentDetails[0].expireDate,
          paidAmountQuota: 0,
          pendingPaymentsAmount: pendingPaymentAmount,
          pendingPaidInFullDocuments: pendingPaidInFullDocuments,
          reservation,
        })
      }
    })
    return result
  }

  const sortRows = (field: string, sort: GridSortDirection, rows: UnpaidDocumentRow[]) => {
    const updRows = [...rows]
    const asc = sort === 'asc' ? 1 : -1
    switch (field) {
      case 'numberExtended':
        updRows.sort((a, b) => (a.numberExtended > b.numberExtended ? 1 : a.numberExtended < b.numberExtended ? -1 : 0) * asc)
        break
      case 'documentDate':
        updRows.sort((a, b) => (a.documentDate > b.documentDate ? 1 : a.documentDate < b.documentDate ? -1 : 0) * asc)
        break
      case 'expireDate':
        updRows.sort((a, b) => (a.expireDate > b.expireDate ? 1 : a.expireDate < b.expireDate ? -1 : 0) * asc)
        break
    }
    return updRows
  }

  const putDocumentIdOnTop = (rows: UnpaidDocumentRow[], documentId: string) => {
    const updRows = [...rows]
    updRows.sort((a, b) => {
      if (a.id === documentId) return -1
      if (b.id === documentId) return 1
      return 0
    })
    return updRows
  }

  useEffect(() => {
    const initialRows = initializeRows(unpaidDocuments, unpaidPaymentActives, paidInFullDocumentsQuotas)
    if (!documentIdOnTop) {
      setSortModel([{ field: 'expireDate', sort: 'asc' }])
      setRows(sortRows('expireDate', 'asc', initialRows))
    } else {
      setRows(putDocumentIdOnTop(initialRows, documentIdOnTop))
    }
  }, [unpaidDocuments, documentIdOnTop, paidInFullDocumentsQuotas, unpaidPaymentActives])

  useEffect(() => {
    if (paidAmount) {
      if (!isTouch) {
        setRows(calculateQuotas(paidAmount))
      } else
        confirm({
          title: t('pms.accounting.documents.payment_distribution.confirm_title'),
          message: t('pms.accounting.documents.payment_distribution.confirm_message'),
          confirmButtonText: t('commons.yes'),
          cancelButtonText: t('commons.no'),
          onConfirm: () => setRows(calculateQuotas(paidAmount)),
        })
    }
  }, [paidAmount])

  const onCellEditCommitHandler = useCallback(
    (newRow: any) => {
      const updRows = rows.map((r) => (r.id === newRow.id ? newRow : r))
      setRows(updRows)
      const updPaidTotalAmount = updRows.reduce((total, current) => {
        return total + current.paidAmountQuota
      }, 0)
      if (localPaidAmount !== updPaidTotalAmount) {
        setLocalPaidAmount(updPaidTotalAmount)
        onTotalPaidAmountChange(updPaidTotalAmount)
      }

      onChangeHandler(updRows)
    },
    [rows, localPaidAmount, onTotalPaidAmountChange, onChangeHandler],
  )

  const onBlurHandler = () => {
    setIsTouch(true)
  }

  const columns = useColumns(onCellEditCommitHandler, onBlurHandler, readonly)

  const handleSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setSortModel(sortModel)
      if (sortModel.length) {
        const { field, sort } = sortModel[0]
        setRows(sortRows(field, sort, rows))
      }
    },
    [rows],
  )

  const getRowClassName = useCallback(
    (params): string => {
      if (params.id === documentIdOnTop) return 'tsh-bold-row'
      return ''
    },
    [documentIdOnTop],
  )

  return (
    <>
      <HicDataGridPro //
        columns={columns}
        rows={rows}
        editMode="row"
        autoHeight
        processRowUpdate={onCellEditCommitHandler}
        hideFooter
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        getRowClassName={getRowClassName}
      />
      <HICError>{error}</HICError>
    </>
  )
}

const useColumns = (onCellEditCommitHandler: (newRow: any) => void, onBlurHandler: () => void, readonly?: boolean): GridColDef[] => {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatTableCurrency } = useFormatCurrency()
  const formatDate = useFormatDate()

  const field = keysOf<UnpaidDocumentRow>()
  return [
    {
      field: field('numberExtended'),
      headerName: t('pms.accounting.documents.progressive'),
      flex: 1,
      sortable: true,
      valueFormatter: ({ value }) => value,
      disableColumnMenu: true,
    },
    {
      field: field('documentDate'),
      headerName: t('pms.accounting.documents.document_date'),
      type: 'date',
      width:100,
      valueFormatter: ({ value }) => formatDate(value),
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'reservation',
      headerName: t('pms.accounting.documents.reservation'),
      sortable: false,
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row.reservation as UnpaidDocumentReservationFragment

        if (row) {
          return (
            <Stack>
              {row?.voucher?.length ? row.voucher : row.reservationCode}
              <Box>{row.reference.mainGuest.name}</Box>
            </Stack>
          )
        } else {
          return null
        }
      },
    },
    {
      field: field('totalAmount'),
      headerName: t('commons.document_total'),
      type: 'number',
      width: 120,
      valueFormatter: ({ value }) => formatTableCurrency(value ?? 0, currency),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: field('unpaidAmount'),
      headerName: t('commons.unpaid_amount'),
      type: 'number',
      width: 120,
      valueFormatter: ({ value }) => formatTableCurrency(value ?? 0, currency),
      renderCell: (params) => {
        const row = params.row as UnpaidDocumentRow
        return (
          <Stack alignItems={'flex-end'}>
            <Box>{formatTableCurrency(params.value ?? 0, currency)}</Box>
            {row.pendingPaymentsAmount ? (
              <Tooltip
                title={
                  <div>
                    {t('pms.accounting.documents.pending_payments_tooltip', { pendingAmount: formatTableCurrency(row.pendingPaymentsAmount ?? 0, currency) })}
                    <Divider light />
                    {row.pendingPaidInFullDocuments?.quotas.map((i, k) => (
                      <div key={k}>{`${i.documentNumber}: ${formatTableCurrency(i.amount, currency)}`}</div>
                    ))}
                  </div>
                }
              >
                <span>
                  <Stack direction="row" spacing={1} justifyContent={'space-between'}>
                    <FontAwesomeIcon icon={faHourglassHalf} />
                    <Typography variant="captionSmall">{formatTableCurrency(row.pendingPaymentsAmount ?? 0, currency)}</Typography>
                  </Stack>
                </span>
              </Tooltip>
            ) : undefined}
          </Stack>
        )
      },
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: field('paidAmountQuota'),
      headerName: t('commons.paid_quota'),
      type: 'number',
      width:130,
      renderCell: (params) => {
        const row = params.row as UnpaidDocumentRow
        if (readonly) return formatTableCurrency(params.value ?? 0, currency)
        return (
          <HicAmountField //
            fullWidth
            value={params.value ?? ''}
            nonNegative
            currency={currency}
            onChange={(v) => onCellEditCommitHandler({ ...row, paidAmountQuota: v })}
            onBlur={onBlurHandler}
            error={params.value > row.unpaidAmount}
          />
        )
      },
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: field('residualAmount'),
      headerName: t('commons.residual_amount'),
      type: 'number',
      width: 120,
      valueGetter: (params) => {
        const row = params.row as UnpaidDocumentRow
        return row.unpaidAmount - row.paidAmountQuota
      },
      valueFormatter: ({ value }) => formatTableCurrency(value ?? 0, currency),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: field('expireDate'),
      headerName: t('commons.unpaid_expire_date'),
      type: 'date',
      width: 120,
      valueFormatter: ({ value }) => formatDate(value),
      cellClassName: (params) => {
        const row = params.row as UnpaidDocumentRow
        if (compareDates(row.expireDate, new Date()) < 0) {
          return 'tsh-alert-cell'
        }
        return ''
      },
      sortable: true,
      disableColumnMenu: true,
    },
  ]
}
